import { PickleballCourtComponent } from './pickleball-court/pickleball-court.component';
import { PlayerCellComponent } from './player-cell/player-cell.component';
import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core';
import { Point } from '../../_shared/models/point.model';
import { Game, GameFormat } from '../../_shared/models/game.model';
import { WinReasonEnum } from '../../_shared/models/csTypes/enums/win_reason';
import { TimeItem } from '../../_shared/models/csTypes/time_item';
import { Infringement } from '../../_shared/models/infringements.model';
import { InfringementType } from '../../_shared/models/enums/infringement-type';
import { Set } from '../../_shared/models/set.model';
import { DisciplineEnum } from '../../_shared/models/csTypes/enums/discipline';
import { ScoreFormat, SetType } from '../../_shared/models/score-format.model';
import { Side } from '../../_shared/models/match.model';
import { AuthService } from '../../auth/auth.service';
import { CourtSide } from '../../_shared/models/enums/court-side';
import { NgClass } from '@angular/common';

@Component({
  selector: 'app-point-row',
  templateUrl: './point-row.component.html',
  styleUrls: ['./point-row.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    PlayerCellComponent,
    PickleballCourtComponent
  ]
})

export class PointRowComponent implements OnInit {
  authService = inject(AuthService);
  @Input({ required: true }) point!: Point;
  @Input({ required: true }) game!: Game;
  @Input({ required: true }) sets!: Set[];
  @Input({ required: true }) sides!: Side[];
  @Input({ required: false }) isGameRow = false;
  @Input({ required: true }) timeItems!: TimeItem[];
  @Input({ required: true }) selectedSet!: number;
  @Input({ required: true }) discipline!: DisciplineEnum;
  @Input({ required: true }) scoreFormat!: ScoreFormat;
  @Input({ required: false }) showScore: boolean = true;
  @Input({ required: true }) playerCellType: 'score-list' | 'official' = 'score-list';
  @Input({ required: false }) areSidesReveresed!: boolean;
  @Input({ required: false }) matchType!: string;
  @Input({ required: false }) matchStatus!: boolean;

  pt = {
    0: '0',
    1: '15',
    2: '30',
    3: '40',
    4: 'A'
  };

  showServerNumber = false;

  getOfficialSign(sideNumber: number) {
    let value = '';

    if (this.point.winReason === 'A' && this.point.winningSide === sideNumber) value = 'A';
    else if (this.point.winReason === 'DF' && this.point.winningSide === sideNumber) value = 'D';
    else if (this.point.winReason === WinReasonEnum.PENALTY) {
        const codeSideNumber = sideNumber === 1 ? 2 : 1;
        const timeItem: TimeItem = this.timeItems.find(
            ti => {
                if (ti.itemValue.pointNumber === this.point.pointNumber &&
                    ti.itemValue.gameNumber === this.game.gameNumber &&
                    ti.itemValue.setNumber === this.selectedSet &&
                    ti.itemValue.sideNumber === codeSideNumber) {
                    return ti;
                }
            });

        if (timeItem) {
            const violation: Infringement = timeItem.itemValue;
            if (violation.infringementType === InfringementType.CODE_VIOLATION) value = 'C';
        }
    }
    else if (this.point.winningSide === sideNumber) value = '/';

    return value;
  }

  isViolation(sideNumber: number): string {
    const violation = this.timeItems?.find(ti =>
        ti.itemValue.sideNumber === sideNumber &&
        ti.itemValue.pointNumber === this.point?.pointNumber &&
        ti.itemValue.gameNumber === this.game?.gameNumber &&
        ti.itemValue.setNumber === this.selectedSet);

        return violation?.itemValue.infringementType.charAt(0);
  }

    /**
   * Checks if the score should be displayed numerically (not tennis 15/30/40)
   */
  private isNumericScoringDiscipline(): boolean {
    const numericDisciplines = [
      DisciplineEnum.PICKLEBALL,
      DisciplineEnum.BADMINTON,
      DisciplineEnum.TABLE_TENNIS,
      DisciplineEnum.SQUASH
    ];
    
    return (
      this.game.gameFormat === GameFormat.TIEBREAK ||
      numericDisciplines.includes(this.discipline) ||
      this.scoreFormat.setType === SetType.TIMED
    );
  }

  getScore(score) {
    return this.isNumericScoringDiscipline() ? score : this.pt[score];
  }

  isServer(sideNumber): boolean {
    return this.point.server?.sideNumber === sideNumber && (!this.isGameRow || (this.isGameRow && !this.isBeginningOfGame()));
  }

  isBeginningOfGame() {
    return this.game.side1Score === 0 && this.game.side2Score === 0;
  }

  isFirstServeMissed(): boolean {
    return this.point.shots.length &&
      this.point.shots[0].shotType === 'SE' &&
      (this.point.shots[0].shotOutcome === 'O' || this.point.shots[0].shotOutcome === 'FOOT_FAULT');
  }

  getServerName() {
    const player = this.sides[this.point.server.sideNumber - 1].players[this.point.server.playerNumber - 1]?.participant;
    return `${player.last_name}, ${player.first_name}`
  }

  /**
   * Gets the player number from a specific court side (deuce or ad) for table tennis and squash
   * @param sideNumber The side number (1 or 2)
   * @param courtSide The court side ('deuce' or 'ad')
   * @returns The player number on that court side, or null if not found
   */
  private getPlayerNumberFromCourtSide(sideNumber: number, courtSide: 'deuce' | 'ad'): number | null {
    if (this.discipline !== DisciplineEnum.TABLE_TENNIS && this.discipline !== DisciplineEnum.SQUASH) {
      return null;
    }

    const playerPositions = this.point?.playerPositions;
    const sidePosition = playerPositions?.[`side${sideNumber}`];
    const player = sidePosition?.[courtSide];
    
    return player?.playerNumber ?? null;
  }

  getPlayerNameTopLeft() {
    if (this.point) {
      let playerNumber: number;
      let showServerNumber = false;
      const sideNumber = this.areSidesReveresed ? 2 : 1;

      // For squash doubles, always show server or returner on top
      if (this.discipline === DisciplineEnum.SQUASH && this.point.server) {
        if(this.matchType === 'D'){
          const serverPlayerNum = this.point.server.playerNumber || 1;
          const returnerPlayerNum = this.point.server.returnerPlayerNumber || 1;
          if (this.point.server.sideNumber === sideNumber) {
            // Server is on this side, show server
            playerNumber = serverPlayerNum;
            showServerNumber = true;
          } else if (this.point.server.returnerSideNumber === sideNumber) {
            // Returner is on this side, show returner
            playerNumber = returnerPlayerNum;
          } else {
            // Fallback
            playerNumber = 1;
          }
        } else {
          // For squash singles, show player from this side on top (always player 1 for singles)
          playerNumber = 1;
          if (this.point.server?.sideNumber === sideNumber && this.point.server?.playerNumber === playerNumber) {
            showServerNumber = true;
          }
        }
      } else if (this.discipline === DisciplineEnum.TABLE_TENNIS) {
        // Top left is AD side - get the player who is on AD side from playerPositions
        if (this.matchType === 'S' && this.matchStatus) {
          return null;
        }
        const adPlayerNumber = this.getPlayerNumberFromCourtSide(sideNumber, 'ad');
        if (adPlayerNumber) {
          playerNumber = adPlayerNumber;
          if (this.point.server?.sideNumber === sideNumber && this.point.server?.playerNumber === playerNumber) {
            showServerNumber = true;
          }
        } else {
          // Fallback: if no playerPositions, use Player 2 (AD side)
          playerNumber = this.matchType === 'S' ? 2 : 1;
          if (this.point.server?.sideNumber === sideNumber && this.point.server?.playerNumber === playerNumber) {
            showServerNumber = true;
          }
        }
      } else {
        // This if statement is needed to support legacy code versions 1.98.0 and older where returner data was not available
        if (!this.point.server?.returnerPlayerNumber) {
          playerNumber = this.discipline === DisciplineEnum.BADMINTON && this.matchType === 'S' ? 2 : 1;
          if (this.point.server?.sideNumber === sideNumber && this.point.server?.playerNumber === playerNumber) {
            showServerNumber = true;
          }
        } else {
          if (this.point.server.returningSide === CourtSide.AD) {
            if (this.point.server.sideNumber === sideNumber) {
              playerNumber = this.point.server.playerNumber || 1;
              showServerNumber = true;
            } else {
              playerNumber = this.point.server.returnerPlayerNumber || 1;
            }
          } else {
            if (this.matchType === 'S') {
              return null;
            }
            if (this.point.server.sideNumber === sideNumber) {
              playerNumber = this.point.server.playerNumber === 1 ? 2 : 1
            } else {
              playerNumber = this.point.server.returnerPlayerNumber === 1 ? 2 : 1;
            }
          }
        }
      }

      const person = this.sides[sideNumber - 1].players[playerNumber - 1]?.participant;

      return {
        person,
        showServerNumber
      };
    }

    return null;
  }

  getPlayerNameBottomLeft() {
    if (this.point) {

      let playerNumber: number;
      let showServerNumber = false;
      const sideNumber = this.areSidesReveresed ? 2 : 1;

      // For squash, always show the other player (not server/returner) on bottom
      if (this.discipline === DisciplineEnum.SQUASH && this.point.server) {
        if (this.matchType === 'S') {
          return null;
        }
        if (this.matchType === 'D') {
        
          const serverPlayerNum = this.point.server.playerNumber || 1;
          const returnerPlayerNum = this.point.server.returnerPlayerNumber || 1;
          if (this.point.server.sideNumber === sideNumber) {
            // Server is on this side, show the other player (not server)
            playerNumber = serverPlayerNum === 1 ? 2 : 1;
          } else if (this.point.server.returnerSideNumber === sideNumber) {
            // Returner is on this side, show the other player (not returner)
            playerNumber = returnerPlayerNum === 1 ? 2 : 1;
          } else {
            // Fallback
            playerNumber = 2;
          }
        }  
      } else if (this.discipline === DisciplineEnum.TABLE_TENNIS) {
        // Top left is AD side - get the player who is on AD side from playerPositions
        const deucePlayerNumber = this.getPlayerNumberFromCourtSide(sideNumber, 'deuce');
        if (deucePlayerNumber) {
          playerNumber = deucePlayerNumber;
          if (this.point.server?.sideNumber === sideNumber && this.point.server?.playerNumber === playerNumber) {
            showServerNumber = true;
          }
        } else {
          // Fallback: if no playerPositions, use Player 1 (deuce side)
          playerNumber = this.matchType === 'S' ? 1 : 2;
          if (this.point.server?.sideNumber === sideNumber && this.point.server?.playerNumber === playerNumber) {
            showServerNumber = true;
          }
        }
      }  else {
        // This if statement is needed to support legacy code versions 1.98.0 and older where returner data was not available
        if (!this.point.server?.returnerPlayerNumber) {
          playerNumber = this.discipline === DisciplineEnum.BADMINTON && this.matchType === 'S' ? 1 : 2;
          if (this.point.server?.sideNumber === sideNumber && this.point.server?.playerNumber === playerNumber) {
            showServerNumber = true;
          }
        } else {
          if (this.point.server.returningSide === CourtSide.DEUCE) {
            if (this.point.server.sideNumber === sideNumber) {
              playerNumber = this.point.server.playerNumber || 1;
              showServerNumber = true;
            } else {
              playerNumber = this.point.server.returnerPlayerNumber || 1;
            }
          } else {
            if (this.matchType === 'S') {
              return null;
            }
            playerNumber = this.point.server.sideNumber === sideNumber
              ? this.point.server.playerNumber === 1 ? 2 : 1
              : this.point.server.returnerPlayerNumber === 1 ? 2 : 1;
          }
        }
      }
      const person = this.sides[sideNumber - 1].players[playerNumber - 1]?.participant;

      return {
        person,
        showServerNumber
      };
    }

    return null;
  }

  getPlayerNameTopRight() {
    if (this.point) {

      let playerNumber: number;
      let showServerNumber = false;
      const sideNumber = this.areSidesReveresed ? 1 : 2;

      // For squash doubles, always show server or returner on top
      if (this.discipline === DisciplineEnum.SQUASH && this.point.server) {
        if(this.matchType === 'D'){
          const serverPlayerNum = this.point.server.playerNumber || 1;
          const returnerPlayerNum = this.point.server.returnerPlayerNumber || 1;
          if (this.point.server.sideNumber === sideNumber) {
            // Server is on this side, show server
            playerNumber = serverPlayerNum;
            showServerNumber = true;
          } else if (this.point.server.returnerSideNumber === sideNumber) {
            // Returner is on this side, show returner
            playerNumber = returnerPlayerNum;
          } else {
            // Fallback
            playerNumber = 1;
          }
        } else {
          // For squash singles, show player from this side on top (always player 1 for singles)
          playerNumber = 1;
          if (this.point.server?.sideNumber === sideNumber && this.point.server?.playerNumber === playerNumber) {
            showServerNumber = true;
          }
        }
      } else if (this.discipline === DisciplineEnum.TABLE_TENNIS) {        
        const adPlayerNumber = this.getPlayerNumberFromCourtSide(sideNumber, 'deuce');
        if (adPlayerNumber) {
          playerNumber = adPlayerNumber;
          if (this.point.server?.sideNumber === sideNumber && this.point.server?.playerNumber === playerNumber) {
            showServerNumber = true;
          }
        } else {
          // Fallback: if no playerPositions, use Player 2 (ad side)
          playerNumber = 1;
          if (this.point.server?.sideNumber === sideNumber && this.point.server?.playerNumber === 1) {
            showServerNumber = true;
          }
        }
      }  else {
        // This if statement is needed to support legacy code versions 1.98.0 and older where returner data was not available
        if (!this.point.server?.returnerPlayerNumber) {
          playerNumber = 1;
          if (this.point.server?.sideNumber === sideNumber && this.point.server?.playerNumber === 1) {
            showServerNumber = true;
          }
        } else {
          if (this.point.server.returningSide === CourtSide.DEUCE) {
            if (this.point.server.sideNumber === sideNumber) {
              playerNumber = this.point.server.playerNumber || 1;
              showServerNumber = true;
            } else {
              playerNumber = this.point.server.returnerPlayerNumber || 1;
            }
          } else {
            playerNumber = this.point.server.sideNumber === sideNumber
              ? (this.point.server.playerNumber || 1) === 1 ? 2 : 1
              : (this.point.server.returnerPlayerNumber || 1) === 1 ? 2 : 1
          }
        }
      }

      const person = this.sides[sideNumber - 1].players[playerNumber - 1]?.participant;

      return {
        person,
        showServerNumber
      };
    }

    return null;
  }

  getPlayerNameBottomRight() {

    if (this.point) {
      let playerNumber: number;
      let showServerNumber = false;
      const sideNumber = this.areSidesReveresed ? 1 : 2;

      // For squash doubles, always show the other player (not server/returner) on bottom
      if (this.discipline === DisciplineEnum.SQUASH && this.point.server) {
        if (this.matchType === 'S') {
          return null;
        }
        if (this.matchType === 'D') {
          const serverPlayerNum = this.point.server.playerNumber || 1;
          const returnerPlayerNum = this.point.server.returnerPlayerNumber || 1;
          if (this.point.server.sideNumber === sideNumber) {
            // Server is on this side, show the other player (not server)
            playerNumber = serverPlayerNum === 1 ? 2 : 1;
          } else if (this.point.server.returnerSideNumber === sideNumber) {
            // Returner is on this side, show the other player (not returner)
            playerNumber = returnerPlayerNum === 1 ? 2 : 1;
          } else {
            // Fallback
            playerNumber = 2;
          }
        }
      } else if (this.discipline === DisciplineEnum.TABLE_TENNIS) {
        if (this.matchType === 'S' && this.matchStatus) {
          return null;
        }

        const deucePlayerNumber = this.getPlayerNumberFromCourtSide(sideNumber, 'ad');
        if (deucePlayerNumber) {
          playerNumber = deucePlayerNumber;
          if (this.point.server?.sideNumber === sideNumber && this.point.server?.playerNumber === playerNumber) {
            showServerNumber = true;
          }
        } else {
          // Fallback: if no playerPositions, use Player 1 (deuce side)
          playerNumber = 2;
          if (this.point.server?.sideNumber === sideNumber && this.point.server?.playerNumber === 2) {
            showServerNumber = true;
          }
        }
      }  else {
        // This if statement is needed to support legacy code versions 1.98.0 and older where returner data was not available
        if (!this.point.server?.returnerPlayerNumber) {
          playerNumber = 2;
          if (this.point.server?.sideNumber === sideNumber && this.point.server?.playerNumber === 2) {
            showServerNumber = true;
          }
        } else {
          if (this.point.server.returningSide === CourtSide.AD) {
            if (this.point.server.sideNumber === sideNumber) {
              playerNumber = this.point.server.playerNumber || 1;
              showServerNumber = true;
            } else {
              playerNumber = this.point.server.returnerPlayerNumber || 1;
            }
          } else {
            playerNumber = this.point.server.sideNumber === sideNumber
              ? this.point.server.playerNumber === 1 ? 2 : 1
              : this.point.server.returnerPlayerNumber === 1 ? 2 : 1
          }
        }
      }

      const person = this.sides[sideNumber - 1].players[playerNumber - 1]?.participant;

      return {
        person,
        showServerNumber
      };
    }

    return null;
  }

  ngOnInit() {
      this.areSidesReveresed = this.point?.extensions?.find(ext => ext.name === 'areSidesReversed')?.value;
  }
}
